import { useEffect, useState } from "react";
import type { AttendeePersonalHome, AttendeeSession } from "@verity-connect/contracts";
import { useAttendeeStateEpoch } from "./cross-device-state";

export type AttendeePersonalHomeState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; home: AttendeePersonalHome }
  | { status: "error"; message: string };

export function useAttendeePersonalHome(session: AttendeeSession | null) {
  const signal = useAttendeeStateEpoch();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<AttendeePersonalHomeState>({ status: "idle" });

  useEffect(() => {
    if (!session) { setState({ status: "idle" }); return; }
    const controller = new AbortController();
    setState((current) => current.status === "ready" ? current : { status: "loading" });
    void fetch("/api/attendee/home", { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Your WVCEA home could not be loaded.");
        setState({ status: "ready", home: await response.json() as AttendeePersonalHome });
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setState({ status: "error", message: reason instanceof Error ? reason.message : "Your WVCEA home could not be loaded." });
      });
    return () => controller.abort();
  }, [attempt, session?.identityId, signal.epoch]);

  return { state, retry: () => setAttempt((value) => value + 1) };
}
